import React, { useMemo, useState } from "react";
import { Bug, Camera, CheckCircle2, Loader2, X } from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";
import "./BugReportButton.css";

const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024;

export default function BugReportButton() {
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const pageUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}${window.location.pathname}${window.location.search}`;
  }, []);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setScreenshot(null);
    setScreenshotPreview("");
    setStatus("idle");
    setMessage("");
  };

  const close = () => {
    setOpen(false);
    window.setTimeout(resetForm, 150);
  };

  const handleScreenshotChange = (event) => {
    const file = event.target.files?.[0] || null;
    setMessage("");
    setScreenshot(null);
    setScreenshotPreview("");

    if (!file) return;

    if (!file.type?.startsWith("image/")) {
      setMessage("Please choose an image file for the screenshot.");
      return;
    }

    if (file.size > MAX_SCREENSHOT_BYTES) {
      setMessage("Screenshots must be 10 MB or smaller.");
      return;
    }

    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!isAuthenticated) {
      setMessage("Please sign in first so we can follow up on the bug report.");
      return;
    }

    if (!description.trim()) {
      setMessage("Please describe what went wrong.");
      return;
    }

    setStatus("submitting");
    setMessage("");

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      });

      const form = new FormData();
      form.append("title", title.trim() || "Bug report");
      form.append("description", description.trim());
      form.append("pageUrl", pageUrl);
      if (typeof navigator !== "undefined") {
        form.append("userAgent", navigator.userAgent || "");
      }
      if (screenshot) {
        form.append("screenshot", screenshot);
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/BugReports`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: form,
      });

      if (!response.ok) {
        const problem = await response.json().catch(() => null);
        throw new Error(problem?.detail || problem?.title || `Bug report failed (${response.status})`);
      }

      setStatus("submitted");
      setMessage("Thanks — your bug report was submitted to Bug Reporting.");
    } catch (error) {
      setStatus("idle");
      setMessage(error.message || "We could not submit the bug report.");
    }
  };

  return (
    <>
      <button
        type="button"
        className="bug-report-fab"
        onClick={() => setOpen(true)}
        aria-label="Report a bug"
        title="Report a bug"
      >
        <Bug size={20} aria-hidden="true" />
        <span>Bug</span>
      </button>

      {open && (
        <div className="bug-report-backdrop" role="presentation">
          <section
            className="bug-report-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bug-report-title"
          >
            <button
              type="button"
              className="bug-report-close"
              onClick={close}
              aria-label="Close bug report form"
            >
              <X size={18} aria-hidden="true" />
            </button>

            <div className="bug-report-heading">
              <span className="bug-report-icon" aria-hidden="true">
                <Bug size={22} />
              </span>
              <div>
                <h2 id="bug-report-title">Report a bug</h2>
                <p>Tell us what broke. A screenshot helps a lot.</p>
              </div>
            </div>

            {status === "submitted" ? (
              <div className="bug-report-success" role="status">
                <CheckCircle2 size={32} aria-hidden="true" />
                <p>{message}</p>
                <button type="button" className="bug-report-primary" onClick={close}>
                  Done
                </button>
              </div>
            ) : (
              <form className="bug-report-form" onSubmit={submit}>
                {!isAuthenticated && (
                  <div className="bug-report-notice">
                    Please sign in before submitting so the team knows who to follow up with.
                    <button
                      type="button"
                      onClick={() => loginWithRedirect({ appState: { returnTo: pageUrl } })}
                    >
                      Sign in
                    </button>
                  </div>
                )}

                <label>
                  Short title
                  <input
                    type="text"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    placeholder="What broke?"
                    maxLength={140}
                  />
                </label>

                <label>
                  What happened?
                  <textarea
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder="What were you trying to do, what happened instead, and what should have happened?"
                    rows={5}
                    required
                  />
                </label>

                <label>
                  Page
                  <input type="text" value={pageUrl} readOnly />
                </label>

                <label className="bug-report-file">
                  <Camera size={18} aria-hidden="true" />
                  <span>{screenshot ? screenshot.name : "Attach screenshot"}</span>
                  <input type="file" accept="image/*" onChange={handleScreenshotChange} />
                </label>

                {screenshotPreview && (
                  <img
                    className="bug-report-preview"
                    src={screenshotPreview}
                    alt="Selected bug report screenshot preview"
                  />
                )}

                {message && <p className="bug-report-message" role="alert">{message}</p>}

                <div className="bug-report-actions">
                  <button type="button" className="bug-report-secondary" onClick={close}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bug-report-primary"
                    disabled={status === "submitting" || !isAuthenticated}
                  >
                    {status === "submitting" ? (
                      <>
                        <Loader2 size={16} className="bug-report-spin" aria-hidden="true" />
                        Sending
                      </>
                    ) : (
                      "Submit bug"
                    )}
                  </button>
                </div>
              </form>
            )}
          </section>
        </div>
      )}
    </>
  );
}
